import { z } from "zod";

/**
 * `MemorySchema.name` — the PAD. One named field group inside a scope.
 *
 * ## 🔴 A PAD IS AN AUTHORISATION SCOPE AND A NAMED FIELD GROUP. IT IS NOT A STORAGE
 * PARTITION, AND READING IT AS ONE INVERTS THE FEATURE
 *
 * Before pads there was exactly one declaration per `(organizationId, scope)` — so one
 * field list per organisation, which cannot serve two agents that record different things
 * about the same customer. The pad is the axis that fixes that.
 *
 * What it does NOT do is divide the stored record. `AgentMemoryEntry` is keyed
 * `@@unique([memoryId, key])` and the read
 * (`FindAgentMemoryBySubjectAndOrgPort(organizationId, subject)`) is UNFILTERED and stays
 * that way. The notes belong to the CUSTOMER, not to the agent that wrote them: a clone,
 * a new version and a replacement agent all keep working against the same stored
 * document. A pad says which keys a given writer MAY WRITE and what that writer is TOLD
 * those keys mean. It never says which keys a reader may see.
 *
 * 🔴 SO TWO PADS SHARING A KEY IS THE CORRECT SEMANTICS AND NOT A COLLISION TO PREVENT.
 * Because the read is already flat and shared, `status` on a customer is ONE fact about
 * that customer whichever pad declares it. What must be refused is not the SHARING but
 * the DISAGREEMENT — two active pads describing one key differently, so the model behind
 * one agent is told it is a number while the other is told it is free text. That refusal
 * lives at ACTIVATION (`ActivateMemorySchemaVersionUseCase`), which is the only moment
 * the active set changes, and it compares exactly the two fields that reach a model.
 *
 * ⚠️ PREFIXING STORED KEYS BY PAD WAS CONSIDERED AND IS REFUSED. It gives each agent a
 * private store, which is the "notes belong to the agent" model this feature exists to
 * reject; it makes the STORED key a function of the pad NAME, so renaming a pad orphans
 * every entry written under it, silently; and it spends the pad name against
 * `AgentMemory_memoryBytes_bounded` on every entry, since the charge counts
 * `octet_length(key)`.
 *
 * The FORM is `MemorySchema_scope_shape`'s, verbatim — one shape for both name axes on
 * this row, so `orange_commande_nouvel_abonnement` is a legal pad exactly as it is a legal
 * scope. The 64 bound mirrors `MemorySchema_name_bounded`
 * (`octet_length("name") BETWEEN 1 AND 64`) and is exact rather than approximate: the
 * shape admits only `[a-z0-9_]`, every one a single UTF-8 byte, so `octet_length` and
 * `.length` cannot disagree for any string that passes it.
 */
export const MEMORY_SCHEMA_PAD_NAME_SHAPE = /^[a-z][a-z0-9_]*$/;

export const MemorySchemaPadNameSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(MEMORY_SCHEMA_PAD_NAME_SHAPE);

/**
 * Whether a pad's fields hold ONE value or ACCUMULATE.
 *
 * 🚨 DECLARED AND NOT YET HONOURED AT THE WRITE DOOR. `single` is today's behaviour for
 * both values: the write door upserts one row per `(memoryId, key)` and latest value wins.
 * A pad declared `multiple` therefore behaves exactly like `single` right now, and
 * `MemorySchema_cardinality_membership` is what stops an unhandled third value existing
 * when it stops being true.
 *
 * ⚠️ HONOURING `multiple` IS NOT A WRITE-DOOR BRANCH, WHICH IS WHY IT IS NOT IN THIS
 * CHANGE. `AgentMemoryEntry` carries `@@unique([memoryId, key])`, so accumulation needs a
 * second key column; the door's compare-and-set predicate pins ONE entry's stored charge
 * by key (`… AND e."key" = $key`) and would become a SUM over rows; the renderer emits one
 * line per key; and `readMemoryToolCall` reads one value per property. Four surfaces, each
 * with its own proof.
 *
 * 🔴 MEMBERSHIP, NOT FORM — AND THE ASYMMETRY WITH `scope` AND `name` IS DELIBERATE. Those
 * two pin only shape, because their catalogs are designed to grow without an `ALTER TYPE`
 * and a retired-but-well-formed value must still parse. This set is closed and the write
 * door will BRANCH on it, so a well-formed unknown value would be a state with no handler.
 */
export const MEMORY_SCHEMA_CARDINALITIES = ["single", "multiple"] as const;

export const MemorySchemaCardinalitySchema = z.enum(MEMORY_SCHEMA_CARDINALITIES);

export type MemorySchemaCardinality = z.infer<typeof MemorySchemaCardinalitySchema>;

/**
 * The GRANT: which pads one agent may WRITE to.
 *
 * It is stored in `AgentToolConfig.config` on the row of type `MEMORY`. That column was
 * previously unread for this type — the enum's own docblock says *"a MEMORY config names
 * NO ROW"* — and that stays true. A pad name is not a row id: it is resolved to whichever
 * version is ACTIVE at the moment of the write, so the grant cannot pin an agent to a
 * retired declaration, which is the property `MemorySchema_one_active` exists to give and
 * the reason that docblock refused to store a `MemorySchema` id here.
 *
 * ## 🔴 A GRANT, NOT OWNERSHIP, AND IT DOES NOT SCOPE READS
 *
 * Revoking a pad from an agent stops that agent WRITING those keys. It removes nothing
 * from the customer's record and hides nothing from any reader, including this agent's own
 * prompt block — the read port is unfiltered and stays that way. That asymmetry is the
 * feature: an agent cloned to experiment on something unrelated keeps every note the
 * original took, because the notes were never the agent's.
 *
 * ⚠️ SO DUPLICATION CARRIES THE GRANT. `PrismaAgentRepository.duplicate` copies `config`
 * and RE-DERIVES `agentInputSchema` against the DESTINATION organisation — a copied grant
 * naming a pad the destination has never declared derives `{}` and the tool says so, where
 * copying the source's derived schema would advertise field names nobody in the
 * destination declared and refuse every write against them.
 *
 * ## ⚠️ LENIENT ON READ, STRICT ON WRITE — the same asymmetry the declaration carries
 *
 * `.min(1)` here is the WRITE shape: naming zero pads is not a state a caller can mean,
 * because such a row advertises nothing and can write nothing. On READ,
 * {@link readMemoryToolGrant} falls back to NO pads rather than throwing, so a row stored
 * before this axis existed keeps loading. That fallback is fail-closed and recoverable:
 * the tool is advertised with no parameters and refuses every call in words an operator
 * can act on, and re-attaching with a pad named fixes it. It is the same answer
 * `resolve-memory-tool-schema.ts` already gives for a pad with no active declaration —
 * attaching memory before declaring a field is a legitimate order of operations, and
 * refusing the attach would order the two steps for no reason.
 *
 * 🔴 THIS IS NOT THE `assertKeyIsDeclared` CASE AND THE TWO POINT OPPOSITE WAYS ON
 * PURPOSE. That value decides whether a WRITE is PERMITTED, so every default there is an
 * authorisation taken by absence and it REFUSES. This one decides only what is
 * ADVERTISED — and a write is still checked against the pad's active declaration inside
 * the write transaction, so a permissive read here grants nothing.
 */
export const MemoryToolGrantSchema = z.object({
  pads: z.array(MemorySchemaPadNameSchema).min(1)
});

export type MemoryToolGrant = z.infer<typeof MemoryToolGrantSchema>;

/**
 * The pads a stored `AgentToolConfig.config` grants, over an unknown column value.
 *
 * Returns `[]` for anything it cannot read — see the write/read asymmetry above. Duplicate
 * names collapse, because a pad named twice grants exactly what it grants once and every
 * consumer here is a set membership test.
 */
export function readMemoryToolGrant(config: unknown): readonly string[] {
  const parsed = MemoryToolGrantSchema.safeParse(config);
  return parsed.success ? [...new Set(parsed.data.pads)] : [];
}
